import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  Unsubscribe,
  updateDoc,
  where,
} from 'firebase/firestore';
import { User as FirebaseUser } from 'firebase/auth';
import { db } from '../platform/firebase/clients';
import {
  LlmCommentSeverity,
  LlmCommentStatus,
  TaskComment,
  TaskCommentFieldAnchor,
  TaskCommentFieldAnchorState,
  TaskCommentKind,
  TaskCommentSource,
} from '../types';
import { wrapSubscriptionError } from './subscriptionError';
import { isTaskCommentFieldAnchor } from '../utils/taskCommentAnchors';

const COMMENTS = 'taskComments';
const MAX_COMMENT_TEXT_LENGTH = 20 * 1024;
const MAX_COMMENT_FIELD_PATH_LENGTH = 500;
const MAX_COMMENT_TASK_TITLE_LENGTH = 500;
const COMMENT_KINDS = new Set<TaskCommentKind>([
  'note',
  'submission',
  'changes_requested',
  'approved',
  'field',
]);

function normalizeCommentText(text: string): string {
  if (typeof text !== 'string') throw new Error('Comment text must be a string.');
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Comment text cannot be empty.');
  if (trimmed.length > MAX_COMMENT_TEXT_LENGTH) {
    throw new Error('Comment text is too long.');
  }
  return trimmed;
}

function normalizeCommentFieldPath(kind: TaskCommentKind, fieldPath: string | null | undefined): string | null {
  if (fieldPath == null || fieldPath.trim() === '') {
    if (kind === 'field') throw new Error('Field comments require a field path.');
    return null;
  }
  const trimmed = fieldPath.trim();
  if (trimmed.length > MAX_COMMENT_FIELD_PATH_LENGTH) {
    throw new Error('Comment field path is too long.');
  }
  if (kind !== 'field') throw new Error('Only field comments may include a field path.');
  return trimmed;
}

function toDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return new Date(0);
}

function toDateOrNull(value: unknown): Date | null {
  if (value == null) return null;
  return toDate(value);
}

function mapSource(raw: unknown): TaskCommentSource | undefined {
  if (raw === 'llm' || raw === 'human') return raw;
  return undefined;
}

function mapLlmStatus(raw: unknown): LlmCommentStatus | undefined {
  if (raw === 'pending' || raw === 'approved') return raw;
  return undefined;
}

function mapSeverity(raw: unknown): LlmCommentSeverity | undefined {
  if (raw === 'info' || raw === 'warning' || raw === 'error') return raw;
  return undefined;
}

function mapFieldAnchorState(raw: unknown): TaskCommentFieldAnchorState | undefined {
  if (raw === 'needs-review' || raw === 'orphaned') return raw;
  return undefined;
}

function mapComment(id: string, data: Record<string, any>): TaskComment {
  const source = mapSource(data.source);
  const comment: TaskComment = {
    id,
    taskId: data.taskId ?? '',
    taskTitle: data.taskTitle ?? '',
    authorId: data.authorId ?? '',
    authorName: data.authorName ?? '',
    text: data.text ?? '',
    kind: (data.kind ?? 'note') as TaskCommentKind,
    fieldPath: typeof data.fieldPath === 'string' ? data.fieldPath : null,
    parentCommentId:
      typeof data.parentCommentId === 'string' ? data.parentCommentId : null,
    reviewRound: typeof data.reviewRound === 'number' ? data.reviewRound : 0,
    addressed: data.addressed === true,
    addressedBy: typeof data.addressedBy === 'string' ? data.addressedBy : null,
    addressedByName: typeof data.addressedByName === 'string' ? data.addressedByName : null,
    addressedAt: toDateOrNull(data.addressedAt),
    createdAt: toDate(data.createdAt),
  };
  if (source) comment.source = source;
  const llmStatus = mapLlmStatus(data.llmStatus);
  if (llmStatus) comment.llmStatus = llmStatus;
  if (typeof data.model === 'string' && data.model) comment.model = data.model;
  if (typeof data.reviewerPluginId === 'string' && data.reviewerPluginId) {
    comment.reviewerPluginId = data.reviewerPluginId;
  }
  if (Number.isSafeInteger(data.reviewerPluginVersion) && data.reviewerPluginVersion > 0) {
    comment.reviewerPluginVersion = data.reviewerPluginVersion;
  }
  if (typeof data.modelPolicyVersion === 'string' && data.modelPolicyVersion) {
    comment.modelPolicyVersion = data.modelPolicyVersion;
  }
  const severity = mapSeverity(data.severity);
  if (severity) comment.severity = severity;
  if (typeof data.editedBy === 'string') comment.editedBy = data.editedBy;
  if (typeof data.editedByName === 'string') comment.editedByName = data.editedByName;
  const editedAt = toDateOrNull(data.editedAt);
  if (editedAt) comment.editedAt = editedAt;
  if (data.fieldSnapshot && typeof data.fieldSnapshot === 'object') {
    comment.fieldSnapshot = {
      value: data.fieldSnapshot.value,
      missing: data.fieldSnapshot.missing === true,
      capturedAt: toDateOrNull(data.fieldSnapshot.capturedAt),
      reviewRound: typeof data.fieldSnapshot.reviewRound === 'number'
        ? data.fieldSnapshot.reviewRound
        : 0,
    };
  }
  if (isTaskCommentFieldAnchor(data.fieldAnchor)) {
    comment.fieldAnchor = data.fieldAnchor;
  }
  const fieldAnchorState = mapFieldAnchorState(data.fieldAnchorState);
  if (fieldAnchorState) comment.fieldAnchorState = fieldAnchorState;
  return comment;
}

export interface AddCommentInput {
  taskId: string;
  taskTitle: string;
  kind: TaskCommentKind;
  text: string;
  user: FirebaseUser;
  reviewRound: number;
  fieldPath?: string | null;
  fieldAnchor?: TaskCommentFieldAnchor;
  /** Pass a root comment id to make this write a reply; omit for a root. */
  parentCommentId?: string | null;
}

export async function addComment(input: AddCommentInput): Promise<void> {
  const {
    taskId,
    taskTitle,
    kind,
    text,
    user,
    reviewRound,
    fieldPath,
    fieldAnchor,
    parentCommentId,
  } = input;
  if (typeof taskId !== 'string' || !taskId.trim()) {
    throw new Error('Comment task id is required.');
  }
  const normalizedTaskId = taskId.trim();
  if (typeof taskTitle !== 'string' || !taskTitle.trim()) {
    throw new Error('Comment task title is required.');
  }
  const normalizedTaskTitle = taskTitle.trim();
  if (normalizedTaskTitle.length > MAX_COMMENT_TASK_TITLE_LENGTH) {
    throw new Error('Comment task title is too long.');
  }
  if (!COMMENT_KINDS.has(kind)) throw new Error('Comment kind is invalid.');
  if (!Number.isSafeInteger(reviewRound) || reviewRound < 0) {
    throw new Error('Comment review round is invalid.');
  }
  const normalizedText = normalizeCommentText(text);
  const normalizedFieldPath = normalizeCommentFieldPath(kind, fieldPath);
  if (fieldAnchor != null && !isTaskCommentFieldAnchor(fieldAnchor)) {
    throw new Error('Comment field anchor is invalid.');
  }
  if (fieldAnchor != null && kind !== 'field') {
    throw new Error('Only field comments may include a field anchor.');
  }
  const normalizedParent = parentCommentId?.trim() || null;
  try {
    await addDoc(collection(db, COMMENTS), {
      taskId: normalizedTaskId,
      taskTitle: normalizedTaskTitle,
      authorId: user.uid,
      authorName: user.displayName ?? user.email ?? 'Unknown',
      kind,
      source: 'human',
      fieldPath: normalizedFieldPath,
      ...(fieldAnchor ? { fieldAnchor } : {}),
      parentCommentId: normalizedParent,
      reviewRound,
      text: normalizedText,
      addressed: false,
      addressedBy: null,
      addressedByName: null,
      addressedAt: null,
      createdAt: serverTimestamp(),
    });
  } catch (err: any) {
    // The Firestore JS SDK retries transient Commit RPC failures internally;
    // if the original RPC committed but the response was lost, the retry hits
    // the auto-id's `exists=false` precondition and surfaces as `already-
    // exists`. The comment is in the database — treat as success instead of
    // bubbling a scary error to the user (and to Error Reporting).
    if (err?.code === 'already-exists') return;
    throw err;
  }
}

export async function deleteTaskComment(commentId: string): Promise<void> {
  // Firestore rules gate this on `authorId == uid()` or admin. The UI
  // hides the affordance from non-authors, but the rule is the real guard.
  await deleteDoc(doc(db, COMMENTS, commentId));
}

export async function markCommentAddressed(
  commentId: string,
  user: FirebaseUser,
  addressed: boolean,
): Promise<void> {
  if (typeof addressed !== 'boolean') throw new Error('Addressed state must be boolean.');
  await updateDoc(doc(db, COMMENTS, commentId), {
    addressed,
    addressedBy: addressed ? user.uid : null,
    addressedByName: addressed ? user.displayName ?? user.email ?? 'Unknown' : null,
    addressedAt: addressed ? serverTimestamp() : null,
  });
}

export function subscribeToComments(
  taskId: string,
  onChange: (comments: TaskComment[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COMMENTS),
    where('taskId', '==', taskId),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => mapComment(d.id, d.data()))),
    wrapSubscriptionError('taskComments.subscribeToComments', onError),
  );
}

// ---- LLM reviewer comment actions ----
//
// These wrap updateDoc/deleteDoc with reviewer-provenance metadata so the
// UI doesn't have to scatter identical "set editedBy/At/By" snippets next
// to every call site. The security rules refuse edits that touch anything
// outside the allowlist (text/llmStatus/edited*), so these helpers mirror
// that allowlist — keeping the client honest matches the server's intent.

function editorStamp(user: FirebaseUser) {
  return {
    editedBy: user.uid,
    editedByName: user.displayName ?? user.email ?? 'Unknown',
    editedAt: serverTimestamp(),
  };
}

/** Flip an LLM comment from `pending` to `approved`. Records who approved
 *  as the editor so the UI can surface attribution consistently. */
export async function approveLlmComment(
  commentId: string,
  user: FirebaseUser,
): Promise<void> {
  await updateDoc(doc(db, COMMENTS, commentId), {
    llmStatus: 'approved',
    ...editorStamp(user),
  });
}

/** Edit an LLM comment's text. Preserves `source: 'llm'` (the field isn't
 *  touched) so the audit trail remains "AI flagged → human refined". */
export async function editLlmComment(
  commentId: string,
  user: FirebaseUser,
  text: string,
): Promise<void> {
  const normalizedText = normalizeCommentText(text);
  await updateDoc(doc(db, COMMENTS, commentId), {
    text: normalizedText,
    ...editorStamp(user),
  });
}

/** Delete an LLM comment outright (used when the reviewer disagrees with
 *  the AI's suggestion). The security rules allow any reviewer with access
 *  to the campaign to delete. Admins can delete any comment. */
export async function deleteLlmComment(commentId: string): Promise<void> {
  await deleteDoc(doc(db, COMMENTS, commentId));
}

/**
 * Edit your own comment's text. Uses the same `editedBy*` stamp as LLM
 * moderation so the audit trail is uniform — `editedByName` on a comment
 * whose `authorName` matches is simply "author revised their own text".
 * Firestore rules already permit `resource.data.authorId == uid()` to
 * update their own row; no rule changes needed.
 */
export async function editOwnComment(
  commentId: string,
  user: FirebaseUser,
  text: string,
): Promise<void> {
  const normalizedText = normalizeCommentText(text);
  await updateDoc(doc(db, COMMENTS, commentId), {
    text: normalizedText,
    ...editorStamp(user),
  });
}
